/**
 * Marketplace Template Card — wraps shared `<PageGridCard/>`.
 *
 * Slots: banner (type-tinted glyph or preview image) → title + type pill →
 * author/version meta → description → stats row → footer (category + CTA).
 *
 * Single source of truth: used by both the marketplace page and the dialog.
 */
import { useLocalePicker } from '@affine/core/hooks/use-locale-picker';
import { useI18n } from '@affine/i18n';
import { PlusIcon } from '@blocksuite/icons/rc';
import { type ReactElement, type ReactNode } from 'react';

import {
  MarketplacePreviewMedia,
  selectMarketplacePreview,
} from '../../pages/workspace/_shared/marketplace-preview-media';
import {
  PageGridCard,
  PageGridCardCategoryChip,
  PageGridCardGlyph,
  PageGridCardTypePill,
  type PageGridCardVariant,
} from '../../pages/workspace/_shared/page-grid-card';
import {
  AgentIcon,
  AppIcon,
  AutomationIcon,
  CheckIcon,
  DocIcon,
  DownloadIcon,
  PlayIcon,
  StarIcon,
  StepIcon,
  WorkflowIcon,
} from './icons';
import { getMarketplaceCategoryLabel } from './marketplace-categories';
import * as styles from './styles.css';
import { isAgentTemplate, isRepeatableBlueprint } from './template-kind';
import type {
  InstalledTemplateInfo,
  MarketplaceTemplate,
} from './use-marketplace-api';

// =============================================================================
// Type config — shared across card + detail-view
// =============================================================================

export interface TypeStyle {
  Glyph: (props: { size?: number }) => ReactElement;
  tint: string;
  label: string;
}

export const TYPE_CONFIG: Record<string, TypeStyle> = {
  app: { Glyph: AppIcon, tint: '#4F46E5', label: 'App' },
  app_block: { Glyph: AppIcon, tint: '#0891B2', label: 'App-Block' },
  doc: { Glyph: DocIcon, tint: '#475569', label: 'Doc' },
  step: { Glyph: StepIcon, tint: '#7C3AED', label: 'Step' },
  workflow: { Glyph: WorkflowIcon, tint: '#2563EB', label: 'Workflow' },
  automation: { Glyph: AutomationIcon, tint: '#0D9488', label: 'Automation' },
};

export const AGENT_TYPE_STYLE: Omit<TypeStyle, 'label'> = {
  Glyph: AgentIcon,
  tint: '#C2410C',
};

export function formatCount(n: number): string {
  if (n >= 1_000_000)
    return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1).replace(/\.0$/, '')}k`;
  return String(n);
}

export function formatRating(rating: number): string {
  return rating > 0 ? rating.toFixed(1) : '—';
}

type TemplateProvenance =
  | 'streamlain_built'
  | 'streamlain_certified'
  | 'community_built';

function resolveTemplateProvenance(
  template: MarketplaceTemplate
): TemplateProvenance {
  if (template.provenance) return template.provenance;
  if (template.publisher?.toLowerCase() === 'streamlain') {
    return 'streamlain_built';
  }
  if (template.verified) return 'streamlain_certified';
  return 'community_built';
}

function provenanceLabel(
  i18n: ReturnType<typeof useI18n>,
  provenance: TemplateProvenance
): string {
  switch (provenance) {
    case 'streamlain_built':
      return i18n['com.streamlain.marketplace.card.trust.streamlain-built']();
    case 'streamlain_certified':
      return i18n[
        'com.streamlain.marketplace.card.trust.streamlain-certified'
      ]();
    case 'community_built':
      return i18n['com.streamlain.marketplace.card.trust.community-built']();
  }
}

function StreamLainBadgeMark(): ReactElement {
  return (
    <svg
      className={styles.cardTrustBadgeLogo}
      viewBox="-65 -74 660 660"
      aria-hidden
      focusable="false"
    >
      <defs>
        <clipPath id="sl-marketplace-badge-wakes">
          <path d="M 130 130 L 214 130 Q 222 130 225 138 Q 232 158 240 178 Q 296 232 352 256 Q 296 280 240 334 Q 232 354 225 374 Q 222 382 214 382 L 130 382 C 90 382 49 329 1 281 Q -11 269 -11 256 Q -11 244 1 231 C 49 183 90 130 130 130 Z" />
        </clipPath>
        <clipPath id="sl-marketplace-badge-inner-chev">
          <path d="M 303 130 Q 291 120 293 136 Q 299 156 321 178 Q 377 232 433 256 Q 377 280 321 334 Q 299 356 293 376 Q 291 392 303 382 Q 379 316 451 256 Q 379 196 303 130 Z" />
        </clipPath>
        <clipPath id="sl-marketplace-badge-outer-chev">
          <path
            fillRule="evenodd"
            d="M 265 54 Q 247 54 249 72 Q 257 96 287 124 Q 363 198 441 256 Q 363 314 287 388 Q 257 416 249 440 Q 247 458 265 458 L 325 458 Q 341 458 353 446 Q 425 372 501 296 Q 521 276 521 256 Q 521 236 501 216 Q 425 140 353 70 Q 341 54 325 54 Z M 303 130 Q 291 120 293 136 Q 299 156 321 178 Q 377 232 433 256 Q 377 280 321 334 Q 299 356 293 376 Q 291 392 303 382 Q 379 316 451 256 Q 379 196 303 130 Z"
          />
        </clipPath>
      </defs>
      <circle cx="265" cy="256" r="280" fill="#ffffff" />
      <g transform="translate(23.42 11.65) scale(0.9474)">
        <g clipPath="url(#sl-marketplace-badge-wakes)">
          <path d="M -29 130 L 381 130 L 381 170 L -29 170 Z" fill="#5090D3" />
          <path d="M -29 228 L 381 228 L 381 284 L -29 284 Z" fill="#5090D3" />
          <path d="M -29 342 L 381 342 L 381 382 L -29 382 Z" fill="#5090D3" />
        </g>
        <g clipPath="url(#sl-marketplace-badge-inner-chev)">
          <path
            fill="#5090D3"
            d="M 303 130 Q 291 120 293 136 Q 299 156 321 178 Q 377 232 433 256 Q 377 280 321 334 Q 299 356 293 376 Q 291 392 303 382 Q 379 316 451 256 Q 379 196 303 130 Z"
          />
        </g>
        <g opacity=".6">
          <path
            fill="#5090D3"
            fillRule="evenodd"
            d="M 265 54 Q 247 54 249 72 Q 257 96 287 124 Q 363 198 441 256 Q 363 314 287 388 Q 257 416 249 440 Q 247 458 265 458 L 325 458 Q 341 458 353 446 Q 425 372 501 296 Q 521 276 521 256 Q 521 236 501 216 Q 425 140 353 70 Q 341 54 325 54 Z M 303 130 Q 291 120 293 136 Q 299 156 321 178 Q 377 232 433 256 Q 377 280 321 334 Q 299 356 293 376 Q 291 392 303 382 Q 379 316 451 256 Q 379 196 303 130 Z"
          />
          <g clipPath="url(#sl-marketplace-badge-outer-chev)">
            <rect
              x="241"
              y="50"
              width="286"
              height="412"
              fill="#5090D3"
              opacity=".55"
            />
          </g>
        </g>
        <path
          fill="#5090D3"
          fillRule="evenodd"
          d="M 269 58 Q 251 58 253 74 Q 259 96 287 124 Q 361 200 437 256 Q 361 312 287 388 Q 259 416 253 438 Q 251 454 269 454 L 321 454 Q 337 454 349 442 Q 421 372 497 296 Q 517 276 517 256 Q 517 236 497 216 Q 421 140 349 70 Q 337 58 321 58 Z M 303 130 Q 291 120 293 136 Q 299 156 321 178 Q 377 232 433 256 Q 377 280 321 334 Q 299 356 293 376 Q 291 392 303 382 Q 379 316 451 256 Q 379 196 303 130 Z"
        />
      </g>
      <circle
        cx="265"
        cy="256"
        r="280"
        stroke="#5090D3"
        strokeWidth="1.5"
        vectorEffect="non-scaling-stroke"
        fill="none"
        transform="rotate(-90 265 256)"
      />
    </svg>
  );
}

// =============================================================================
// Component
// =============================================================================

export interface TemplateCardProps {
  template: MarketplaceTemplate;
  assetWorkspaceId?: string | null;
  onSelect: () => void;
  onInstall: () => void;
  onUpdate?: () => void;
  installDisabled: boolean;
  installedInfo?: InstalledTemplateInfo;
  /** Mark as StreamLain Featured — visually distinct from community uploads. */
  featured?: boolean;
}

export interface TemplateCardFrameProps {
  template: MarketplaceTemplate;
  assetWorkspaceId?: string | null;
  featured?: boolean;
  installedVersionLabel?: string | null;
  bodyExtra?: ReactNode;
  /** Additional status rendered in the shared banner's top-right badge stack. */
  cornerBadge?: ReactNode;
  /** Optional control rendered at the top-right of the card body. */
  bodyBadge?: ReactNode;
  footerEnd?: ReactNode;
  onClick?: () => void;
  variant?: PageGridCardVariant;
  testId?: string;
  disabled?: boolean;
  ariaPressed?: boolean;
  ariaBusy?: boolean;
}

/** Creation placeholder using the same miniature frame as installed templates. */
export function AppBlockCreationCard({
  onClick,
  disabled,
  expanded,
}: {
  onClick: () => void;
  disabled?: boolean;
  expanded: boolean;
}): ReactElement {
  const { Glyph, tint, label } = TYPE_CONFIG.app_block;
  return (
    <PageGridCard
      variant="miniature"
      tint={tint}
      banner={
        <PageGridCardGlyph variant="miniature">
          <PlusIcon width={28} height={28} />
        </PageGridCardGlyph>
      }
      title="Eigener App-Block"
      description="Leer starten und anschließend mit der KI gestalten."
      typePill={
        <PageGridCardTypePill>
          <Glyph size={12} />
          {label}
        </PageGridCardTypePill>
      }
      footerEnd={
        <button
          type="button"
          className={styles.installButton}
          disabled={disabled}
          onClick={event => {
            event.stopPropagation();
            onClick();
          }}
        >
          {expanded ? 'Schließen' : 'App-Block erstellen'}
        </button>
      }
      ariaLabel="Eigener App-Block"
      ariaPressed={expanded}
      disabled={disabled}
      testId="custom-app-create-app-block-card"
      onClick={onClick}
    />
  );
}

export function TemplateCardFrame({
  template,
  assetWorkspaceId,
  featured,
  installedVersionLabel: installedVersionLabelInput,
  bodyExtra,
  cornerBadge,
  bodyBadge,
  footerEnd,
  onClick,
  variant = 'default',
  testId,
  disabled,
  ariaPressed,
  ariaBusy,
}: TemplateCardFrameProps): ReactElement {
  const i18n = useI18n();
  const picker = useLocalePicker();

  const typeKey = template.templateType ?? 'workflow';
  const isAgent = isAgentTemplate(template);
  const provenance = resolveTemplateProvenance(template);
  const baseTypeStyle = TYPE_CONFIG[typeKey] ?? TYPE_CONFIG.workflow;
  const typeStyle = isAgent ? AGENT_TYPE_STYLE : baseTypeStyle;
  const TypeGlyph = typeStyle.Glyph;
  const typeLabelKey =
    `com.streamlain.marketplace.card.type.${typeKey}` as keyof typeof i18n;
  const typeLabel = isAgent
    ? i18n['com.streamlain.marketplace.card.type.agent']()
    : typeof i18n[typeLabelKey] === 'function'
      ? i18n[typeLabelKey]()
      : baseTypeStyle.label;

  const description = picker?.pickDescription(template.descriptionsI18n) ?? '';
  const categoryLabel = getMarketplaceCategoryLabel(i18n, template.category);

  const downloads = template.downloadCount ?? 0;
  const executionCount = template.executionCount ?? 0;
  const rating = template.averageRating ?? 0;
  const installedVersionLabel =
    installedVersionLabelInput ?? resolveLegacyInstalledVersionLabel(template);
  const assetBasePath = assetWorkspaceId
    ? `/api/workspace/${assetWorkspaceId}/marketplace/blobs/${template.id}`
    : `/api/marketplace/blobs/${template.id}`;
  const cardPreview = selectMarketplacePreview(template.previewSet, {
    role: 'card_cover',
    variant: 'desktop',
    routeKey: isAgent
      ? 'agent'
      : typeKey === 'step'
        ? 'step'
        : typeKey === 'doc'
          ? 'document'
          : typeKey,
  });

  // Banner content priority:
  //   1. Step icon blob (step templates with custom icon)
  //   2. Preview image blob (any template with uploaded preview)
  //   3. Type glyph (fallback — tinted tile + watermark)
  const banner = cardPreview ? (
    <MarketplacePreviewMedia artifact={cardPreview} alt="" allowMotion />
  ) : typeKey === 'step' && template.stepIconBlobId ? (
    <PageGridCardGlyph variant={variant}>
      <img
        src={`${assetBasePath}/icon`}
        alt=""
        style={{ width: 44, height: 44, objectFit: 'contain' }}
      />
    </PageGridCardGlyph>
  ) : template.previewImageBlobId ? (
    <img
      src={`${assetBasePath}/preview`}
      alt=""
      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
    />
  ) : (
    <PageGridCardGlyph variant={variant}>
      <TypeGlyph size={28} />
    </PageGridCardGlyph>
  );

  const trustBadge =
    provenance !== 'community_built' ? (
      <span
        className={
          provenance === 'streamlain_built'
            ? `${styles.cardTrustBadge} ${styles.cardTrustBadgeIconOnly}`
            : styles.cardTrustBadge
        }
        title={provenanceLabel(i18n, provenance)}
        aria-label={provenanceLabel(i18n, provenance)}
      >
        {provenance === 'streamlain_built' && (
          <span className={styles.cardTrustBadgeLogoFrame}>
            <StreamLainBadgeMark />
          </span>
        )}
        {provenance !== 'streamlain_built' && provenanceLabel(i18n, provenance)}
      </span>
    ) : null;

  const bannerBadge =
    featured || isAgent || cornerBadge ? (
      <span className={styles.cardBadgeStack}>
        {featured && (
          <span className={styles.cardFeaturedBadge}>
            <StarIcon size={11} />{' '}
            {i18n['com.streamlain.marketplace.card.featured']()}
          </span>
        )}
        {isAgent && (
          <span className={styles.cardFeaturedBadge}>
            {i18n['com.streamlain.marketplace.card.badge.agent']()}
          </span>
        )}
        {cornerBadge}
      </span>
    ) : null;

  const meta =
    template.publishedByName || installedVersionLabel != null ? (
      <>
        {template.publishedByName && (
          <span>
            {i18n['com.streamlain.marketplace.card.author']({
              name: template.publishedByName,
            })}
          </span>
        )}
        {template.publishedByName && installedVersionLabel != null && (
          <span className={styles.cardAuthorDot}>·</span>
        )}
        {installedVersionLabel != null && <span>{installedVersionLabel}</span>}
      </>
    ) : null;

  const defaultStats = (
    <div className={styles.cardStats}>
      <span className={styles.cardStatItem}>
        <span className={styles.cardStatIcon}>
          <DownloadIcon />
        </span>
        {formatCount(downloads)}
      </span>
      <span className={styles.cardStatItem}>
        <span className={styles.cardStatIcon}>
          <StarIcon />
        </span>
        {formatRating(rating)}
      </span>
      {executionCount > 0 && (
        <span className={styles.cardStatItem}>
          <span className={styles.cardStatIcon}>
            <PlayIcon />
          </span>
          {formatCount(executionCount)}
        </span>
      )}
    </div>
  );

  return (
    <PageGridCard
      variant={variant}
      tint={typeStyle.tint}
      banner={banner}
      bannerBadge={bannerBadge}
      bodyBadge={bodyBadge ?? (variant === 'miniature' ? null : trustBadge)}
      title={picker?.pickTitle(template.titlesI18n) ?? ''}
      typePill={
        <PageGridCardTypePill>
          <TypeGlyph size={12} />
          {typeLabel}
        </PageGridCardTypePill>
      }
      meta={variant === 'miniature' ? null : meta}
      description={description}
      bodyExtra={
        variant === 'miniature' ? bodyExtra : (bodyExtra ?? defaultStats)
      }
      footerStart={
        <PageGridCardCategoryChip title={categoryLabel}>
          {categoryLabel}
        </PageGridCardCategoryChip>
      }
      footerEnd={footerEnd}
      onClick={onClick}
      ariaLabel={picker?.pickTitle(template.titlesI18n) ?? template.id}
      testId={testId}
      resourceId={template.id}
      disabled={disabled}
      ariaPressed={ariaPressed}
      ariaBusy={ariaBusy}
    />
  );
}

export function TemplateCard({
  template,
  assetWorkspaceId,
  onSelect,
  onInstall,
  installDisabled,
  installedInfo,
  featured,
}: TemplateCardProps): ReactElement {
  const i18n = useI18n();
  const isPreinstalled = template.isPreinstalled === true;
  const legacyInstalled =
    template.installedVersionNumber != null &&
    template.installedVersionNumber > 0;
  const isInstalled =
    isPreinstalled || installedInfo != null || legacyInstalled;
  const canAddAnotherFromMarketplace = isRepeatableBlueprint(template);

  const footerEnd =
    isInstalled && canAddAnotherFromMarketplace ? (
      <button
        type="button"
        className={styles.installButton}
        disabled={installDisabled}
        onClick={e => {
          e.stopPropagation();
          onInstall();
        }}
        title={
          installDisabled
            ? i18n['com.streamlain.marketplace.card.install.upgrade']()
            : i18n['com.streamlain.marketplace.card.add-again.tooltip']()
        }
      >
        {i18n['com.streamlain.marketplace.card.add-again']()}
      </button>
    ) : isInstalled ? (
      <span className={styles.installedBadge}>
        <CheckIcon /> {i18n['com.streamlain.marketplace.card.installed']()}
      </span>
    ) : (
      <button
        type="button"
        className={styles.installButton}
        disabled={installDisabled}
        onClick={e => {
          e.stopPropagation();
          onInstall();
        }}
        title={
          installDisabled
            ? i18n['com.streamlain.marketplace.card.install.upgrade']()
            : i18n['com.streamlain.marketplace.card.install.tooltip']()
        }
      >
        {i18n['com.streamlain.marketplace.card.install']()}
      </button>
    );

  return (
    <TemplateCardFrame
      template={template}
      assetWorkspaceId={assetWorkspaceId}
      featured={featured}
      installedVersionLabel={resolveInstalledVersionLabel(
        template,
        installedInfo
      )}
      footerEnd={footerEnd}
      onClick={onSelect}
    />
  );
}

function resolveInstalledVersionLabel(
  template: MarketplaceTemplate,
  installedInfo: InstalledTemplateInfo | undefined
): string | null {
  if (installedInfo) {
    if (
      typeof installedInfo.semanticVersion === 'string' &&
      installedInfo.semanticVersion.length > 0
    ) {
      return installedInfo.semanticVersion;
    }
    throw new Error(
      `Marketplace install ${installedInfo.installId} is missing semanticVersion`
    );
  }
  return resolveLegacyInstalledVersionLabel(template);
}

function resolveLegacyInstalledVersionLabel(
  template: MarketplaceTemplate
): string | null {
  if (template.installedVersionNumber == null) return null;
  const semanticVersion = template.versions?.find(
    version => version.versionNumber === template.installedVersionNumber
  )?.semanticVersion;
  if (typeof semanticVersion === 'string' && semanticVersion.length > 0) {
    return semanticVersion;
  }
  throw new Error(
    `Marketplace template ${template.id} is missing semanticVersion for installed version ${template.installedVersionNumber}`
  );
}
